import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  BookOpen,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  UserCheck,
  UserX,
  Mail,
  Phone,
  Building,
  Filter,
  GraduationCap,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  MapPin,
  Briefcase
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  getDashboardAnalytics, 
  getUniversities, 
  createUniversity,
  getCourses,
  createCourse,
  getUsers,
  getPendingUsers,
  approveUser,
  rejectUser,
  getApplications,
  updateApplicationStatus,
  getCommissions,
  updateCommissionStatus
} from '@/services/api';
import { toast } from 'sonner';

// Stats Card Component
const StatsCard = ({ title, value, icon, trend, trendValue, color = 'primary' }) => {
  const colorClasses = {
    primary: 'text-[#00D4FF] bg-[#00D4FF]/10',
    accent: 'text-[#8B5CF6] bg-[#8B5CF6]/10',
    success: 'text-[#10B981] bg-[#10B981]/10',
    warning: 'text-[#F59E0B] bg-[#F59E0B]/10',
  };

  return (
    <Card className="bg-[#141029] border-[#2D2654]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#6B678A] mb-1">{title}</p>
            <p className="text-2xl font-semibold text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {value}
            </p>
            {trend && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend === 'up' ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Overview Page
const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const data = await getDashboardAnalytics();
      setStats(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00D4FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Overview of your platform performance</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Universities"
          value={stats?.total_universities || 0}
          icon={<Building2 className="w-5 h-5" strokeWidth={1.5} />}
          color="primary"
        />
        <StatsCard
          title="Total Courses"
          value={stats?.total_courses || 0}
          icon={<BookOpen className="w-5 h-5" strokeWidth={1.5} />}
          color="accent"
        />
        <StatsCard
          title="Active Agents"
          value={stats?.total_agents || 0}
          icon={<Users className="w-5 h-5" strokeWidth={1.5} />}
          color="success"
        />
        <StatsCard
          title="Total Applications"
          value={stats?.total_applications || 0}
          icon={<FileText className="w-5 h-5" strokeWidth={1.5} />}
          color="warning"
        />
      </div>

      {/* Application Status Breakdown */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardHeader>
          <CardTitle className="text-[white] text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Application Status Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats?.application_status_breakdown || {}).map(([status, count]) => (
              <div key={status} className="p-4 rounded-lg bg-[#0D0B1E] border border-[#2D2654]">
                <p className="text-xs text-[#6B678A] uppercase tracking-wider mb-1">
                  {status.replace('_', ' ')}
                </p>
                <p className="text-xl font-semibold text-[white]">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Commission Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatsCard
          title="Total Commission Value"
          value={`$${(stats?.total_commission_value || 0).toLocaleString()}`}
          icon={<DollarSign className="w-5 h-5" strokeWidth={1.5} />}
          color="accent"
        />
        <StatsCard
          title="Paid Commissions"
          value={`$${(stats?.paid_commissions || 0).toLocaleString()}`}
          icon={<CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />}
          color="success"
        />
      </div>
    </div>
  );
};

// Universities Page
const AdminUniversities = () => {
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUni, setNewUni] = useState({
    name: '', country: '', city: '', ranking: '', tuition_min: '', tuition_max: '', acceptance_rate: ''
  });

  const loadUniversities = useCallback(async () => {
    try {
      const data = await getUniversities();
      setUniversities(data);
    } catch (error) {
      toast.error('Failed to load universities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUniversities();
  }, [loadUniversities]);

  const handleAddUniversity = async () => {
    try {
      await createUniversity({
        ...newUni,
        ranking: newUni.ranking ? parseInt(newUni.ranking) : null,
        tuition_min: newUni.tuition_min ? parseFloat(newUni.tuition_min) : null,
        tuition_max: newUni.tuition_max ? parseFloat(newUni.tuition_max) : null,
        acceptance_rate: newUni.acceptance_rate ? parseFloat(newUni.acceptance_rate) : null,
      });
      toast.success('University added successfully');
      setShowAddModal(false);
      setNewUni({ name: '', country: '', city: '', ranking: '', tuition_min: '', tuition_max: '', acceptance_rate: '' });
      loadUniversities();
    } catch (error) {
      toast.error('Failed to add university');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-universities">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Universities
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Manage partner universities</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogTrigger asChild>
            <Button className="bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]" data-testid="add-university-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add University
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141029] border-[#2D2654] text-[white]">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add New University</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="University Name"
                value={newUni.name}
                onChange={(e) => setNewUni({ ...newUni, name: e.target.value })}
                className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                data-testid="uni-name-input"
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Country"
                  value={newUni.country}
                  onChange={(e) => setNewUni({ ...newUni, country: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="City"
                  value={newUni.city}
                  onChange={(e) => setNewUni({ ...newUni, city: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="World Ranking"
                  type="number"
                  value={newUni.ranking}
                  onChange={(e) => setNewUni({ ...newUni, ranking: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="Acceptance Rate %"
                  type="number"
                  value={newUni.acceptance_rate}
                  onChange={(e) => setNewUni({ ...newUni, acceptance_rate: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Min Tuition (USD)"
                  type="number"
                  value={newUni.tuition_min}
                  onChange={(e) => setNewUni({ ...newUni, tuition_min: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
                <Input
                  placeholder="Max Tuition (USD)"
                  type="number"
                  value={newUni.tuition_max}
                  onChange={(e) => setNewUni({ ...newUni, tuition_max: e.target.value })}
                  className="bg-[#0D0B1E] border-[#2D2654] text-[white]"
                />
              </div>
              <Button 
                onClick={handleAddUniversity} 
                className="w-full bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]"
                data-testid="submit-university-btn"
              >
                Add University
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Universities Table */}
      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">University</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Location</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Ranking</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Tuition Range</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                  <th className="text-right text-sm font-medium text-[#B8B4CE] px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {universities.map((uni, index) => (
                  <tr key={uni.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors" data-testid={`uni-row-${index}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#0D0B1E] border border-[#2D2654] flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[white]">{uni.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-[#B8B4CE]">
                        <Globe className="w-4 h-4" strokeWidth={1.5} />
                        {uni.city}, {uni.country}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[white]">#{uni.ranking || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[#B8B4CE]">
                        ${uni.tuition_min?.toLocaleString() || 'N/A'} - ${uni.tuition_max?.toLocaleString() || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={uni.is_active ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}>
                        {uni.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-[#6B678A] hover:text-[white]">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#141029] border-[#2D2654]">
                          <DropdownMenuItem className="text-[white] focus:bg-[#1A1435]">
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-[white] focus:bg-[#1A1435]">
                            <Edit className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Applications Page
const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadApplications = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const data = await getApplications(params);
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleStatusUpdate = async (appId, newStatus) => {
    try {
      await updateApplicationStatus(appId, newStatus);
      toast.success('Status updated successfully');
      loadApplications();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    submitted: 'bg-[#3B82F6]/20 text-[#60A5FA]',
    under_review: 'bg-[#F59E0B]/20 text-[#FBBF24]',
    documents_required: 'bg-[#EF4444]/20 text-[#F87171]',
    loa_issued: 'bg-[#10B981]/20 text-[#34D399]',
    visa_filed: 'bg-[#8B5CF6]/20 text-[#A78BFA]',
    enrolled: 'bg-[#00D4FF]/20 text-[#00D4FF]',
    rejected: 'bg-[#EF4444]/20 text-[#F87171]'
  };

  return (
    <div className="space-y-6" data-testid="admin-applications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Applications
          </h1>
          <p className="text-[#6B678A] text-sm mt-1">Review and manage student applications</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-[#0D0B1E] border-[#2D2654] text-[white]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-[#141029] border-[#2D2654]">
            <SelectItem value="all" className="text-[white]">All Applications</SelectItem>
            <SelectItem value="submitted" className="text-[white]">Submitted</SelectItem>
            <SelectItem value="under_review" className="text-[white]">Under Review</SelectItem>
            <SelectItem value="loa_issued" className="text-[white]">LOA Issued</SelectItem>
            <SelectItem value="rejected" className="text-[white]">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Student</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Course</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">University</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Date</th>
                  <th className="text-right text-sm font-medium text-[#B8B4CE] px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app, index) => (
                  <tr key={app.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors" data-testid={`app-row-${index}`}>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[white]">{app.student_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#B8B4CE]">{app.course_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#B8B4CE]">{app.university_name}</p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={statusColors[app.status] || 'bg-[#2D2654] text-[#B8B4CE]'}>
                        {app.status?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#6B678A]">
                        {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-[#6B678A] hover:text-[white]">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#141029] border-[#2D2654]">
                          <DropdownMenuItem 
                            className="text-[white] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'under_review')}
                          >
                            <Clock className="w-4 h-4 mr-2" /> Mark Under Review
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[#10B981] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'loa_issued')}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Issue LOA
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[#EF4444] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(app.id, 'rejected')}
                          >
                            <XCircle className="w-4 h-4 mr-2" /> Reject
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {applications.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[#6B678A]">
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Users Page (placeholder)
const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          User Management
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Manage agents and institutions</p>
      </div>

      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">User</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Email</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Role</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Company</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#00D4FF]/20 flex items-center justify-center text-[#00D4FF] font-medium">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <p className="text-sm font-medium text-[white]">{user.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#B8B4CE]">{user.email}</td>
                    <td className="px-6 py-4">
                      <Badge className="bg-[#2D2654] text-[#B8B4CE] capitalize">
                        {user.role?.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#B8B4CE]">{user.company_name || '-'}</td>
                    <td className="px-6 py-4">
                      <Badge className={user.is_active ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Commissions Page
const AdminCommissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCommissions = useCallback(async () => {
    try {
      const data = await getCommissions();
      setCommissions(data);
    } catch (error) {
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCommissions();
  }, [loadCommissions]);

  const handleStatusUpdate = async (commId, newStatus) => {
    try {
      await updateCommissionStatus(commId, newStatus);
      toast.success('Commission status updated');
      loadCommissions();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-commissions">
      <div>
        <h1 className="text-2xl font-medium text-[white]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Commission Management
        </h1>
        <p className="text-[#6B678A] text-sm mt-1">Track and manage agent commissions</p>
      </div>

      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#0D0B1E] border-b border-[#2D2654]">
                <tr>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Agent</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Student</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">University</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Amount</th>
                  <th className="text-left text-sm font-medium text-[#B8B4CE] px-6 py-4">Status</th>
                  <th className="text-right text-sm font-medium text-[#B8B4CE] px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((comm) => (
                  <tr key={comm.id} className="border-b border-[#2D2654] hover:bg-[#1A1435] transition-colors">
                    <td className="px-6 py-4 text-sm text-[white]">{comm.agent_name}</td>
                    <td className="px-6 py-4 text-sm text-[#B8B4CE]">{comm.student_name}</td>
                    <td className="px-6 py-4 text-sm text-[#B8B4CE]">{comm.university_name}</td>
                    <td className="px-6 py-4 text-sm font-medium text-[#8B5CF6]">
                      ${comm.amount.toLocaleString()} {comm.currency}
                    </td>
                    <td className="px-6 py-4">
                      <Badge className={
                        comm.status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        comm.status === 'approved' ? 'bg-[#F59E0B]/20 text-[#FBBF24]' :
                        'bg-[#2D2654] text-[#B8B4CE]'
                      }>
                        {comm.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-[#6B678A] hover:text-[white]">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#141029] border-[#2D2654]">
                          <DropdownMenuItem 
                            className="text-[white] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(comm.id, 'approved')}
                          >
                            Approve
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-[#10B981] focus:bg-[#1A1435]"
                            onClick={() => handleStatusUpdate(comm.id, 'paid')}
                          >
                            Mark as Paid
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-[#6B678A]">
                      No commissions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Placeholder pages
const AdminCourses = () => {
  const [courses, setCourses] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const PAGE_SIZE = 20;
  const [filters, setFilters] = useState({
    country: '', degree_level: '', field_of_study: '', max_tuition: '', intake_month: '', university_id: ''
  });

  const INTAKE_MONTHS = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' },
    { value: '5', label: 'May' }, { value: '7', label: 'July' },
    { value: '8', label: 'August' }, { value: '9', label: 'September' }
  ];

  const fetchUniversities = useCallback(() => {
    getUniversities({ limit: 200 }).then(setUniversities).catch(() => {});
  }, []);

  useEffect(() => { fetchUniversities(); }, [fetchUniversities]);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (filters.country) params.country = filters.country;
      if (filters.degree_level) params.degree_level = filters.degree_level;
      if (filters.field_of_study) params.field_of_study = filters.field_of_study;
      if (filters.max_tuition) params.max_tuition = parseFloat(filters.max_tuition);
      if (filters.intake_month) params.intake_month = parseInt(filters.intake_month);
      if (filters.university_id) params.university_id = filters.university_id;
      if (searchText.trim()) params.search = searchText.trim();
      const data = await getCourses(params);
      setCourses(data.courses || data);
      setTotalCount(data.total_count || (data.courses || data).length);
      setTotalPages(data.total_pages || 1);
    } catch (error) { toast.error('Failed to load courses'); }
    finally { setLoading(false); }
  }, [page, filters, searchText]);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  const handleSearch = () => { setPage(0); loadCourses(); };
  const clearFilters = () => {
    setFilters({ country: '', degree_level: '', field_of_study: '', max_tuition: '', intake_month: '', university_id: '' });
    setSearchText('');
    setPage(0);
    setTimeout(loadCourses, 0);
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length + (searchText ? 1 : 0);
  const monthName = (m) => ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m] || '';
  const filteredUnis = filters.country ? universities.filter(u => u.country === filters.country) : universities;

  return (
    <div className="space-y-5" data-testid="admin-courses">
      <div>
        <h1 className="text-2xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>All Programs & Courses</h1>
        <p className="text-[#6B678A] text-sm mt-1">Manage all programs across institutions</p>
      </div>

      <Card className="bg-[#141029] border-[#2D2654]">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B678A]" />
              <input
                placeholder="Search programs, universities..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full bg-[#0D0B1E] border border-[#2D2654] text-white rounded-md pl-10 pr-3 py-2 text-sm"
                data-testid="admin-course-search"
              />
            </div>
            <Button onClick={() => setShowFilters(!showFilters)} variant="outline" className="border-[#2D2654] text-[#B8B4CE] hover:bg-[#1E1A3A]">
              <Filter className="w-4 h-4 mr-2" /> Filters {activeFilterCount > 0 && <Badge className="ml-2 bg-[#00D4FF] text-[#0F172A] text-xs px-1.5">{activeFilterCount}</Badge>}
            </Button>
            <Button onClick={handleSearch} className="bg-[#00D4FF] text-[#0F172A] hover:bg-[#00B8D4]">
              <Search className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-[#2D2654]">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <Select value={filters.country} onValueChange={(v) => setFilters({ ...filters, country: v, university_id: '' })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white"><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="Canada" className="text-white">Canada</SelectItem>
                    <SelectItem value="United Kingdom" className="text-white">United Kingdom</SelectItem>
                    <SelectItem value="United States" className="text-white">United States</SelectItem>
                    <SelectItem value="Australia" className="text-white">Australia</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.degree_level} onValueChange={(v) => setFilters({ ...filters, degree_level: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white"><SelectValue placeholder="Degree Level" /></SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="diploma" className="text-white">Diploma</SelectItem>
                    <SelectItem value="bachelors" className="text-white">Bachelor's</SelectItem>
                    <SelectItem value="masters" className="text-white">Master's</SelectItem>
                    <SelectItem value="phd" className="text-white">PhD</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.field_of_study} onValueChange={(v) => setFilters({ ...filters, field_of_study: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white"><SelectValue placeholder="Field of Study" /></SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    <SelectItem value="engineering" className="text-white">Engineering & IT</SelectItem>
                    <SelectItem value="business" className="text-white">Business & Commerce</SelectItem>
                    <SelectItem value="health" className="text-white">Health & Medicine</SelectItem>
                    <SelectItem value="arts" className="text-white">Arts & Humanities</SelectItem>
                    <SelectItem value="science" className="text-white">Science</SelectItem>
                    <SelectItem value="hospitality" className="text-white">Hospitality</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filters.intake_month} onValueChange={(v) => setFilters({ ...filters, intake_month: v })}>
                  <SelectTrigger className="bg-[#0D0B1E] border-[#2D2654] text-white"><SelectValue placeholder="Intake" /></SelectTrigger>
                  <SelectContent className="bg-[#141029] border-[#2D2654]">
                    {INTAKE_MONTHS.map(m => (<SelectItem key={m.value} value={m.value} className="text-white">{m.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end">
                <Button onClick={clearFilters} variant="ghost" className="text-[#6B678A] hover:text-white text-sm"><X className="w-3 h-3 mr-1" /> Clear All</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!loading && <p className="text-sm text-[#6B678A]">Showing <span className="text-white font-medium">{courses.length}</span> of <span className="text-white font-medium">{totalCount}</span> programs — Page {page + 1} of {totalPages}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {courses.map((course, i) => (
          <Card key={course.id} className="bg-[#141029] border-[#2D2654] hover:border-[#00D4FF]/30 transition-all" data-testid={`admin-course-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 mr-3">
                  <p className="font-medium text-white">{course.name}</p>
                  <p className="text-sm text-[#6B678A] flex items-center gap-1"><Building2 className="w-3 h-3" />{course.university_name}</p>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <Badge className="bg-[#00D4FF]/20 text-[#00D4FF] capitalize text-xs">{course.degree_level}</Badge>
                  {course.field_of_study && <Badge className="bg-[#8B5CF6]/15 text-[#8B5CF6] capitalize text-xs">{course.field_of_study}</Badge>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                <span className="text-[#10B981] font-medium flex items-center gap-1"><DollarSign className="w-3 h-3" />{course.currency} {course.tuition_fee?.toLocaleString()}/yr</span>
                <span className="text-white flex items-center gap-1"><Clock className="w-3 h-3 text-[#6B678A]" />{course.duration_months} months</span>
                <span className="text-[#B8B4CE]">GPA: {course.min_gpa || 'N/A'} | IELTS: {course.min_ielts || 'N/A'}</span>
                <span className="text-[#B8B4CE] flex items-center gap-1"><Calendar className="w-3 h-3 text-[#6B678A]" />{course.intake_months?.map(m => monthName(m)).join(', ')}</span>
              </div>
              {(course.co_op_available || course.scholarship_available) && (
                <div className="flex gap-2 mt-2">
                  {course.co_op_available && <Badge className="bg-[#10B981]/15 text-[#10B981] text-xs">Co-op</Badge>}
                  {course.scholarship_available && <Badge className="bg-[#F59E0B]/15 text-[#F59E0B] text-xs">Scholarship</Badge>}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {!loading && courses.length > 0 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="border-[#2D2654] text-[#B8B4CE]">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-sm text-[#6B678A]">Page {page + 1}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages} className="border-[#2D2654] text-[#B8B4CE]">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {courses.length === 0 && !loading && (
        <Card className="bg-[#141029] border-[#2D2654]"><CardContent className="py-12 text-center">
          <BookOpen className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white font-medium mb-1">No programs found</p>
          <p className="text-[#6B678A] text-sm">Try adjusting your filters</p>
        </CardContent></Card>
      )}
    </div>
  );
};

const AdminAnalytics = () => (
  <div className="space-y-6">
    <h1 className="text-2xl font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Analytics</h1>
    <p className="text-[#B8B4CE]">Advanced analytics coming soon...</p>
  </div>
);

// Pending Approvals Page
const AdminApprovals = () => {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPendingUsers = useCallback(async () => {
    try {
      const data = await getPendingUsers();
      setPendingUsers(data);
    } catch (error) {
      toast.error('Failed to load pending users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingUsers();
  }, [loadPendingUsers]);

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success('User approved successfully! They can now login.');
      loadPendingUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await rejectUser(userId);
      toast.success('User registration rejected');
      loadPendingUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00D4FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-approvals">
      <div>
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Pending Approvals
        </h1>
        <p className="text-[#B8B4CE] text-sm mt-1">Review and approve new partner registrations</p>
      </div>

      {pendingUsers.length === 0 ? (
        <Card className="bg-[#141029] border-[#2D2654]">
          <CardContent className="py-12 text-center">
            <UserCheck className="w-12 h-12 text-[#6B678A] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#6B678A]">No pending registrations</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {pendingUsers.map((user) => (
            <Card key={user.id} className="bg-[#141029] border-[#2D2654] hover:border-[#8B5CF6]/30 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center text-white font-bold text-xl">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">{user.name}</h3>
                      <Badge className={user.role === 'agent' ? 'bg-[#00D4FF]/20 text-[#00D4FF]' : 'bg-[#8B5CF6]/20 text-[#8B5CF6]'}>
                        {user.role === 'agent' ? 'Recruitment Partner' : 'Institution'}
                      </Badge>
                      
                      <div className="mt-3 space-y-1 text-sm text-[#B8B4CE]">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#6B678A]" />
                          {user.email}
                        </div>
                        {user.company_name && (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-[#6B678A]" />
                            {user.company_name}
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-[#6B678A]" />
                            {user.phone}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-[#6B678A] mt-2">
                        Registered: {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleApprove(user.id)}
                      className="bg-[#10B981] hover:bg-[#059669] text-white"
                      data-testid={`approve-${user.id}`}
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReject(user.id)}
                      variant="outline"
                      className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10"
                      data-testid={`reject-${user.id}`}
                    >
                      <UserX className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Dashboard Component
import CanadaColleges from './CanadaColleges';

const AdminDashboard = () => {
  return (
    <DashboardLayout>
      <Routes>
        <Route path="/" element={<AdminOverview />} />
        <Route path="/approvals" element={<AdminApprovals />} />
        <Route path="/canada-colleges" element={<CanadaColleges />} />
        <Route path="/universities" element={<AdminUniversities />} />
        <Route path="/courses" element={<AdminCourses />} />
        <Route path="/users" element={<AdminUsers />} />
        <Route path="/applications" element={<AdminApplications />} />
        <Route path="/commissions" element={<AdminCommissions />} />
        <Route path="/analytics" element={<AdminAnalytics />} />
      </Routes>
    </DashboardLayout>
  );
};

export default AdminDashboard;
